import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import { toast } from 'react-toastify';
import api from '../../services/api';
import ViewEOIModal from './ViewEOIModal';

const EOIsTable = () => {
    const [eois, setEOIs] = useState([]);
    const [currentEOI, setCurrentEOI] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    // Filters
    const [searchText, setSearchText] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');

    // Fetch EOI data
    const fetchEOIs = async () => {
        try {
            const res = await api.get('/eoi/unpaginated');
            if (res.data.response) {
                setEOIs(res.data.response.data || []);
            }
        } catch (error) {
            console.error('Error fetching EOIs:', error);
            toast.error('Failed to load EOIs');
        }
    };

    useEffect(() => {
        fetchEOIs();
    }, []);

    // ----- View Modal Handlers -----
    const handleView = (eoi) => {
        setCurrentEOI(eoi);
        setShowViewModal(true);
    };

    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setCurrentEOI(null);
        // If needed, refetch data here:
        // fetchEOIs();
    };

    // Status formatting
    const getStatusLabel = (status) => {
        if (status === 'approved') return 'Approved';
        if (status === 'rejected') return 'Rejected';
        return 'Pending';
    };

    const getStatusClass = (status) => {
        if (status === 'approved') return 'success';
        if (status === 'rejected') return 'danger';
        return 'warning';
    };

    // Columns definition
    const columns = [
        {
            name: 'EOI No.',
            selector: (row) => row.id,
            sortable: true,
            width: '90px',
        },
        {
            name: 'Broker Number',
            selector: (row) => row.broker?.mobile || '',
            sortable: true,
        },
        {
            name: 'Developer',
            selector: (row) => row.developer || '',
            sortable: true,
        },
        {
            name: 'Project',
            selector: (row) => row.project?.name || '',
            sortable: true,
        },
        {
            name: 'Unit Type',
            selector: (row) => row.typeOfUnit || '',
            sortable: true,
        },
        {
            name: 'Client Name',
            selector: (row) => row.clientName || '',
            sortable: true,
        },
        {
            name: 'Client Number',
            selector: (row) => row.clientNumber || '',
            sortable: true,
        },
        {
            name: 'Amount Paid',
            selector: (row) => row.amountPaid || '',
            sortable: true,
        },
        {
            name: 'Document',
            cell: (row) =>
                row.document ? (
                    <a
                        href={row.document}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#487fff', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                        View Document
                    </a>
                ) : (
                    'N/A'
                ),
            ignoreRowClick: true,
            sortable: false,
        },
        {
            name: 'Status',
            cell: (row) => (
                <span
                    className={`bg-${getStatusClass(row.status)}-focus text-${getStatusClass(row.status)
                        }-main px-24 py-4 rounded-pill fw-medium text-sm`}
                    style={{ whiteSpace: 'nowrap' }}
                >
                    {getStatusLabel(row.status)}
                </span>
            ),
            sortable: true,
            minWidth: '140px',
        },
        {
            name: 'Actions',
            cell: (row) => (
                <button className="btn btn-primary btn-sm" onClick={() => handleView(row)}>
                    View
                </button>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '80px',
        },
    ];

    // Build distinct status options (approved, rejected, pending, etc.)
    const distinctStatuses = Array.from(new Set(eois.map((item) => item.status))).filter(Boolean);

    // Combine search & status filter
    const filteredData = eois.filter((item) => {
        // Search across multiple fields
        const search = searchText.toLowerCase();
        const matchesSearch =
            !searchText ||
            String(item.id).toLowerCase().includes(search) ||
            (item.broker?.mobile || '').toLowerCase().includes(search) ||
            (item.developer || '').toLowerCase().includes(search) ||
            (item.project?.name || '').toLowerCase().includes(search) ||
            (item.typeOfUnit || '').toLowerCase().includes(search) ||
            (item.clientName || '').toLowerCase().includes(search) ||
            (item.clientNumber || '').toLowerCase().includes(search) ||
            (String(item.amountPaid) || '').toLowerCase().includes(search);

        // Status filter
        const matchesStatus = selectedStatus ? item.status === selectedStatus : true;

        return matchesSearch && matchesStatus;
    });

    // CSV Export
    const csvHeaders = [
        { label: 'EOI No.', key: 'id' },
        { label: 'Broker Number', key: 'broker.mobile' },
        { label: 'Developer', key: 'developer' },
        { label: 'Project', key: 'project.name' },
        { label: 'Unit Type', key: 'typeOfUnit' },
        { label: 'Client Name', key: 'clientName' },
        { label: 'Client Number', key: 'clientNumber' },
        { label: 'Amount Paid', key: 'amountPaid' },
        { label: 'Status', key: 'status' },
        { label: 'Created At', key: 'created_at' },
        { label: 'Updated At', key: 'updated_at' }
    ];

    // Optional custom row styling
    const customStyles = {
        rows: {
            style: {
                fontSize: '15px',
            },
        },
    };

    return (
        <div className="card basic-data-table px-3 py-3">
            <div className="card-body">

                {/* Filters & Export */}
                <div className="d-flex justify-content-start align-items-center mb-3 flex-wrap gap-2">
                    {/* Search Filter */}
                    <input
                        type="text"
                        placeholder="Search EOI..."
                        className="form-control"
                        style={{ maxWidth: '300px' }}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />

                    {/* Status Filter */}
                    <select
                        className="form-control"
                        style={{ maxWidth: '200px' }}
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                    >
                        <option value="">All Statuses</option>
                        {distinctStatuses.map((status) => (
                            <option key={status} value={status}>
                                {getStatusLabel(status)}
                            </option>
                        ))}
                    </select>

                    {/* CSV Export */}
                    <CSVLink
                        data={eois}
                        headers={csvHeaders}
                        filename="eois-data-all.csv"
                        className="btn btn-secondary"
                    >
                        Export All EOIs CSV
                    </CSVLink>
                </div>

                {/* DataTable */}
                <DataTable
                    columns={columns}
                    data={filteredData}
                    pagination
                    highlightOnHover
                    responsive
                    striped
                    customStyles={customStyles}
                    noDataComponent="No results"
                />
            </div>

            {/* View Modal */}
            {showViewModal && (
                <ViewEOIModal
                    show={showViewModal}
                    handleClose={handleCloseViewModal}
                    eoi={currentEOI}
                />
            )}
        </div>
    );
};

export default EOIsTable;
